import axios from 'axios';
import {
    ArchiveNode,
    Article,
    ArticleWithRelations,
    Category,
    CreateDTO,
    KBEntity,
    Section,
    Status,
    Statuses,
    Tag,
    UpdateDTO,
} from './admin-knowledge-base.type';
import { ArticleInfo, Types } from '/react4xp/parts/knowledge-base-common/knowledge-base-common.type';
import { languages } from './admin-knowledge-base.constants';
import { logFunction, sectionCreateConfig, sectionEditConfig, sectionViewConfig } from '../../services/logs';

const BASE_URL = '/_/service/isnareactcms/kb-service';
const TAGS_ROOT = '/tags';

export const getSectionsApi = async () => {
    const res = await logFunction({
        config: {
            ...sectionViewConfig,
            description: 'Получение списка разделов в Базе Знаний',
        },
        wrapperFunction() {
            return axios.get<Section[]>(BASE_URL);
        }
    });
    return res.data;
};

const getEntitiesByParent = async <Entity>(parent: string) => {
    const res = await logFunction({
        config: {
            ...sectionViewConfig,
            description: 'Получение родительских элементов сущностей в Базе Знаний',
        },
        wrapperFunction() {
            return axios.get<Entity>(BASE_URL, { params: { parent } });
        }
    });
    return res.data;
};

export const getById = async <Entity>(id: string) => {
    const res = await logFunction({
        config: {
            ...sectionViewConfig,
            description: 'Получение записи по Id в Базе Знаний',
        },
        wrapperFunction() {
            return axios.get<Entity>(BASE_URL, { params: { id } });
        }
    });
    return res.data;
};

export const getCategoriesApi = getEntitiesByParent<Category[]>;
export const getArticlesApi = getEntitiesByParent<Article[]>;
export const getTagsApi = async () => getEntitiesByParent<Tag[]>(TAGS_ROOT);

export const getArchive = async () => {
    const res = await logFunction({
        config: {
            ...sectionViewConfig,
            description: 'Получение списка архивов в Базе Знаний',
        },
        wrapperFunction() {
            return axios.get<ArchiveNode[]>(BASE_URL, { params: { archive: true } });
        }
    });
    return res.data;
};

export const getSearchResults = async (query: string) => {
    const res = await logFunction({
        config: {
            ...sectionViewConfig,
            description: 'Получение списка по поиску в Базе Знаний',
        },
        wrapperFunction() {
            return axios.get<ArticleWithRelations[]>(BASE_URL, { params: { query } });
        }
    });
    return res.data;
};

export const getArticleNames = async (query: string) => {
    const res = await logFunction({
        config: {
            ...sectionViewConfig,
            description: 'Полученеи списка наименований статей в Базе Знаний',
        },
        wrapperFunction() {
            return axios.get<ArticleInfo[]>(BASE_URL, {
                params: { query, namesOnly: true },
            })
        }
    });
    return res.data;
};

export const createSectionApi = async (sectionDTO: CreateDTO<Section>) => {
    const res = await logFunction({
        config: {
            ...sectionCreateConfig,
            description: 'Создание раздела в Базе Знаний'
        },
        wrapperFunction() {
            return axios.post(BASE_URL, {
                ...sectionDTO,
                type: Types.SECTION,
                status: { current: Statuses.PUBLISH },
                order: 1_000,
            })
        },
        body: sectionDTO,
    });
    return res.data;
};

export const createCategoryApi = async (categoryDTO: CreateDTO<Category>) => {
    const res = await logFunction({
        config: {
            ...sectionCreateConfig,
            description: 'Создание категорий в Базе Знаний'
        },
        wrapperFunction() {
            return axios.post(BASE_URL, {
                ...categoryDTO,
                type: Types.CATEGORY,
                status: { current: Statuses.PUBLISH },
                order: 1_000,
            })
        },
        body: categoryDTO
    });
    return res.data;
};

export const createTagApi = async (tagDTO: CreateDTO<Tag>) => {
    const res = await logFunction({
        config: {
            ...sectionCreateConfig,
            description: 'Создание тэгов в Базе Знаний'
        },
        wrapperFunction() {
            return axios.post(BASE_URL, {
                ...tagDTO,
                type: Types.TAG,
                parent: TAGS_ROOT,
                status: { current: Statuses.PUBLISH },
                order: 1_000,
            })
        },
        body: tagDTO
    });
    return res.data;
};

export const createArticleApi = async (articleDTO: CreateDTO<Article>) => {
    languages.forEach(
        (lang) =>
        (articleDTO[lang].content = articleDTO[lang].content.replaceAll(
            '/admin/rest-v2/cs/cms/ru/content/content/image/',
            'image://'
        ))
    );
    const body = {
        ...articleDTO,
        type: Types.ARTICLE,
        order: 1_000,
    }
    const res = await logFunction({
        config: {
            ...sectionCreateConfig,
            description: 'Создание статьи в Базе Знаний'
        },
        wrapperFunction() {
            return axios.post<Article>(BASE_URL, body)
        },
        body
    });
    return res.data;
};

const updateEntity = async <Entity extends KBEntity>(dto: UpdateDTO<Entity>) => {
    const { id, ...data } = dto;
    const res = await logFunction({
        config: {
            ...sectionEditConfig,
            description: 'Обновление сущности в Базе Знаний',
        },
        wrapperFunction() {
            return axios.put<Entity>(BASE_URL, data, { params: { id } })
        },
        body: data as any,
    });
    return res.data;
};

export const updateSectionApi = updateEntity<Section>;
export const updateCategoryApi = updateEntity<Category>;
export const updateTagApi = updateEntity<Tag>;
export const updateArticleApi = (dto: UpdateDTO<Article>) => {
    languages.forEach((lang) => {
        dto[lang].content = dto[lang].content.replaceAll(
            '/admin/rest-v2/cs/cms/ru/content/content/image/',
            'image://'
        );
    });
    return updateEntity<Article>(dto);
};

export const reorder = async ({ order }: { order: string[] }) => {
    const res = await logFunction({
        config: {
            ...sectionEditConfig,
            description: 'Изменение порядка в Базе Знаний'
        },
        wrapperFunction() {
            return axios.put(BASE_URL, { order });
        },
        body: { order }
    });
    return res.data;
};

const updateStatus = async <Entity extends KBEntity>(id: string, status: Partial<Status>) => {
    const res = await logFunction({
        config: {
            ...sectionEditConfig,
            description: 'Обновление статуса в Базе Знаний'
        },
        wrapperFunction() {
            return axios.put<Entity>(BASE_URL, { status }, { params: { id } })
        },
        body: { status }
    });
    return res.data;
};

export const toArchive = <Entity extends KBEntity>(id: string, effective?: Date) => {
    const newStatus = effective
        ? { planned: Statuses.ARCHIVE, effective }
        : { current: Statuses.ARCHIVE };
    return updateStatus<Entity>(id, newStatus);
};

export const restoreFromArchive = (id: string, status: Statuses) =>
    updateStatus(id, { current: status });

// TODO: for debug purposes, delete before prod
export const clear = async () => {
    const res = await axios.delete(BASE_URL, { params: { clear: true } });
    return res.data;
};
// TODO: for debug purposes, delete before prod
export const listTasks = async () => {
    const res = await axios.delete(BASE_URL, { params: { tasks: true } });
    return res.data;
};
